import { createContext, useState, useContext, FC, ReactNode, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

/* Structure of URL interface */
interface URLContextInfo {
  debug: boolean;
  maxResults: number;
  transactionId: string;
  treeIds:string[]; 
  reportId: string;
  reportType: string;
  metadataId: string;
  runId: string;
  metadataType: string;
  preLoginUrl: string;
  setPreLoginUrl: (url: string) => void;
  clearPreLoginUrl: () => void;
}

/* Context for managing URL */
const URLContext = createContext<URLContextInfo | undefined>(undefined);

/* Custom hook to access URL from the context */
export const useURLContext = (): URLContextInfo => {
  const context = useContext(URLContext);
  if (!context) {
    throw new Error("useURLContext must be used within a URLContextProvider");
  }
  return context;
};

/* Props for the URLContext provider component */
interface URLContextProps {
  children: ReactNode;
}

/* Provider component for managing URL */
export const URLContextProvider: FC<URLContextProps> = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [transactionId, setTransactionId] = useState<string>("");
  const [reportId, setReportId] = useState<string>("");
  const [reportType, setReportType] = useState<string>("");
  const [treeIds, setTreeIds] = useState<string[]>([]);
  const [metadataId, setMetadataId] = useState<string>("");
  const [runId, setRunId] = useState<string>("");
  const [metadataType, setMetadataType] = useState<string>("");
  const [preLoginUrl, setPreLoginUrlState] = useState<string>("");
  const defaultDebug = false;
  const defaultMaxResults = 200;

  // Initialize state from URL parameters or use default values
  const initialDebug = searchParams.get("debug") === "true" ? true : defaultDebug;
  const initialMaxResults = parseInt(searchParams.get("maxResults") || "") || defaultMaxResults;
  const transaction_id = searchParams.get("transaction_id");
  const tree_ids = searchParams.get("tree_ids");
  const report_id = searchParams.get("report_id");
  const report_type = searchParams.get("reportType");
  const metadata_id = searchParams.get("metadataId");
  const metadata_type = searchParams.get("metadataType");
  const run_id = searchParams.get("runId");

  // States to maintain the params in URL for subsequent navigation to other routes
  const [debug, setDebug] = useState(initialDebug);
  const [maxResults, setMaxResults] = useState(initialMaxResults);

  // Functions for pre-login URL management
  const setPreLoginUrl = (url: string) => {
    setPreLoginUrlState(url);
  };

  const clearPreLoginUrl = () => {
    setPreLoginUrlState("");
  };

  // Effect to update URL parameters when state changes
  useEffect(() => {
    const params = new URLSearchParams();

    if (debug !== defaultDebug) {
      params.set("debug", debug.toString());
      setDebug(debug);
    }
    if (maxResults !== defaultMaxResults) {
      params.set("maxResults", maxResults.toString());
      setMaxResults(maxResults);
    }
    if (transaction_id) {
      setTransactionId(transaction_id);
    }
    if(tree_ids){
      params.set("tree_ids", tree_ids);
      const decodedTreeIds = tree_ids.split(",");
      setTreeIds(decodedTreeIds);
    }
    if(report_id) {
      setReportId(report_id);
      params.set("report_id", report_id);
    }
    if(report_type) {
      setReportType(report_type);
      params.set("reportType", report_type);
    }
    if(metadata_id) {
      setMetadataId(metadata_id);
      params.set("metadataId", metadata_id);
    }
    if(run_id) {
      setRunId(run_id);
      params.set("runId", run_id);
    }
    if(metadata_type) {
      setMetadataType(metadata_type);
      params.set("metadataType", metadata_type);
    }
    setSearchParams(params, { replace: true });
  }, [debug, maxResults, transaction_id, tree_ids, report_id, metadata_id, run_id]); // Added initialParam to dependencies

  return (
    <URLContext.Provider 
      value={{ 
        debug, 
        maxResults, 
        transactionId, 
        treeIds, 
        reportId, 
        reportType, 
        metadataId, 
        runId,
        metadataType,
        preLoginUrl,
        setPreLoginUrl,
        clearPreLoginUrl
      }}>
      {children}
    </URLContext.Provider>
  );
};